import { contentAi } from '@mi-front/api-clients';
import { useState, createContext, useContext, useEffect } from 'react';
import { AiArticlesSelector } from './AiArticlesSelector';
import { ArticlesGenerator } from './ArticlesGenerator';
import { PntApiResultStatus } from './PntApiResultStatus';

// Context for tracking internal progress of the ai-articles step
const AiArticlesProgressContext = createContext<{
  progress: number;
  setProgress: (progress: number) => void;
}>({
  progress: 33,
  setProgress: () => {
    // Default implementation - will be overridden by provider
  },
});

export const useAiArticlesProgress = () => useContext(AiArticlesProgressContext);

export const AiArticlesProgressProvider = AiArticlesProgressContext.Provider;

export function ContentWizard_AiArticlesStep({
  content,
  onRegenerate: onRegenerateProp,
}: {
  content: contentAi.ContentDto;
  onRegenerate?: () => void;
}) {
  const [mode, setMode] = useState<'select' | 'regenerate' | null>(null);
  const { setProgress } = useAiArticlesProgress();

  const handleModeChange = (newMode: 'select' | 'regenerate' | null) => {
    setMode(newMode);
  };

  // Update progress based on current view
  useEffect(() => {
    if (!content.aiArticles.data) {
      setProgress(33); // Generator view
    } else if (mode === 'regenerate') {
      setProgress(33); // Regenerate view (generator)
    } else {
      setProgress(66); // Selector view
    }
  }, [content.aiArticles.data, mode, setProgress]);

  // Listen for regenerate event from top button
  useEffect(() => {
    const handleRegenerateEvent = () => {
      handleModeChange('regenerate');
    };

    window.addEventListener('regenerate-articles', handleRegenerateEvent);
    return () => {
      window.removeEventListener('regenerate-articles', handleRegenerateEvent);
    };
  }, []);

  if (!content.pntApiResult.data) {
    return <PntApiResultStatus content={content} />;
  }

  const handleRegenerate = () => {
    handleModeChange('regenerate');
    if (onRegenerateProp) {
      onRegenerateProp();
    }
  };

  if (!content.aiArticles.data) {
    return <ArticlesGenerator content={content} allowInstructions />;
  }

  if (mode === 'regenerate') {
    return (
      <ArticlesGenerator
        content={content}
        allowInstructions
        onSuccess={() => handleModeChange('select')}
      />
    );
  }

  return <AiArticlesSelector content={content} onRegenerate={handleRegenerate} />;
}
